import { useEffect, useState } from 'react';
import { getCountries, getStates, getDistricts, getAptekas, getGeoUnits, addGeoUnit } from '@/lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { seedData } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { X, ChevronDown, MapPin, Map, Building } from 'lucide-react';

export function useStateDistrict() {
  const { user } = useAuth();
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [states, setStates] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [allDistricts, setAllDistricts] = useState([]);
  const [selectedState, setSelectedState] = useState(() => sessionStorage.getItem('pharmx_state') || '');
  const [selectedDistrict, setSelectedDistrict] = useState(() => sessionStorage.getItem('pharmx_district') || 'all');
  const [partnerGeo, setPartnerGeo] = useState(null);

  // Persist filter selections to sessionStorage
  useEffect(() => { if (selectedState) sessionStorage.setItem('pharmx_state', selectedState); }, [selectedState]);
  useEffect(() => { if (selectedDistrict) sessionStorage.setItem('pharmx_district', selectedDistrict); }, [selectedDistrict]);

  const scope = {
    state: user?.assigned_state || null,
    district: user?.assigned_district || null,
    apteka_id: user?.apteka_id || null,
    isSuperAdmin: ['platform_owner', 'platform_admin', 'super_admin', 'company_owner'].includes(user?.role),
  };

  // Load countries + all states on mount
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        let stRes = await getStates();
        if (stRes.data.length === 0) { await seedData(); stRes = await getStates(); }
        let availableStates = stRes.data;
        if (scope.state && !scope.isSuperAdmin) {
          availableStates = availableStates.filter(s => s === scope.state);
        }
        setAllStates(availableStates);
        setStates(availableStates);

        const cRes = await getCountries().catch(() => ({ data: [] }));
        const loadedCountries = cRes.data || [];
        setCountries(loadedCountries);
        if (loadedCountries.length === 0) {
          try { const retry = await getCountries(); setCountries(retry.data || []); } catch {}
        }

        // Only set state if not yet initialized
        setSelectedState(prev => {
          if (prev && prev !== '') return prev;
          if (scope.state && !scope.isSuperAdmin) return scope.state;
          return 'all';
        });

        // Load all districts - extract from aptekas for speed
        const allD = [];
        try {
          const aptekaRes = await getAptekas({});
          const apts = aptekaRes.data || [];
          const seen = new Set();
          const pCountries = new Set(), pStates = new Set(), pDistricts = new Set();
          for (const a of apts) {
            const distName = a.district;
            const stateName = a.state;
            if (a.country) pCountries.add(a.country);
            if (stateName) pStates.add(stateName);
            if (distName) pDistricts.add(distName);
            if (distName && !seen.has(distName)) {
              seen.add(distName);
              allD.push({ name: distName, state: stateName });
            }
          }
          setPartnerGeo({ countries: pCountries, states: pStates, districts: pDistricts });
        } catch {}
        // Also load from API for completeness (just the user's state)
        const primaryState = scope.state || (availableStates.length === 1 ? availableStates[0] : null);
        if (primaryState) {
          try {
            const dRes = await getDistricts(primaryState);
            const dists = (dRes.data || []).map(d => ({ ...d, state: primaryState }));
            for (const d of dists) {
              if (!allD.some(x => x.name === d.name)) allD.push(d);
            }
          } catch {}
        }
        setAllDistricts(allD);
        setDistricts(allD);
      } catch (e) { /* silent */ }
    })();
  }, [user]);

  // When country changes → filter states
  useEffect(() => {
    if (selectedCountry === 'all') {
      setStates(allStates);
    } else {
      (async () => {
        try {
          const res = await getStates(selectedCountry);
          let filtered = (res.data || []);
          if (scope.state && !scope.isSuperAdmin) {
            filtered = filtered.filter(s => s === scope.state);
          }
          setStates(filtered);
        } catch { setStates([]); }
      })();
    }
    // Only reset if no stored selection exists (prevents clearing on mount)
    const storedState = sessionStorage.getItem('pharmx_state');
    const storedDistrict = sessionStorage.getItem('pharmx_district');
    setSelectedState(storedState || 'all');
    setSelectedDistrict(storedDistrict || 'all');
  }, [selectedCountry]);

  // When state changes or allDistricts load → update district options (NOT selection)
  useEffect(() => {
    if (!selectedState) { setDistricts([]); return; }
    if (selectedState === 'all') {
      if (selectedCountry === 'all') {
        setDistricts(allDistricts);
      } else {
        setDistricts(allDistricts.filter(d => states.includes(d.state)));
      }
      return;
    }
    (async () => {
      try {
        const res = await getDistricts(selectedState);
        let dists = (res.data || []).map(d => ({ ...d, state: selectedState }));
        if (scope.district && !scope.isSuperAdmin) {
          dists = dists.filter(d => d.name === scope.district);
        }
        setDistricts(dists);
      } catch { setDistricts([]); }
    })();
  }, [selectedState, allDistricts]);

  return {
    countries, selectedCountry, setSelectedCountry,
    states, districts,
    selectedState, setSelectedState,
    selectedDistrict, setSelectedDistrict,
    scope, partnerGeo,
  };
}

export function GeoMultiPick({ label, options, value, onChange, placeholder, disabled, testId, onAddCustom }) {
  const selected = (value || '').split(',').map(s => s.trim()).filter(Boolean);
  const [custom, setCustom] = useState('');
  const [open, setOpen] = useState(false);
  const toggle = (opt) => {
    const next = selected.includes(opt) ? selected.filter(s => s !== opt) : [...selected, opt];
    onChange(next.join(', '));
  };
  const addCustom = async () => {
    let v = custom.trim();
    if (!v || selected.includes(v)) { setCustom(''); return; }
    if (onAddCustom) {
      try {
        const saved = await onAddCustom(v);
        if (saved && saved.name) v = saved.name;
      } catch { /* keep local value */ }
    }
    if (!selected.includes(v)) onChange([...selected, v].join(', '));
    setCustom('');
  };
  const allOptions = [...new Set([...(options || []), ...selected])];
  return (
    <div>
      {label && <label className="text-[11px] font-medium text-muted-foreground mb-1 block">{label}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} data-testid={testId}
            className={`w-full h-9 px-3 rounded-md border bg-background text-sm text-left flex items-center justify-between gap-2 ${disabled ? 'opacity-50 cursor-default' : 'hover:bg-muted/40'}`}>
            <span className={`truncate ${selected.length ? '' : 'text-muted-foreground'}`}>
              {selected.length ? selected.join(', ') : placeholder}
            </span>
            <ChevronDown className="w-3.5 h-3.5 opacity-60 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0" align="start">
          <div className="max-h-56 overflow-y-auto p-1">
            {allOptions.length === 0 && <p className="text-xs text-muted-foreground px-2 py-2">No options — type your own below</p>}
            {allOptions.map(opt => (
              <label key={opt} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm" data-testid={`${testId}-opt-${opt}`}>
                <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} className="accent-teal-600" />
                <span className="truncate">{opt}</span>
              </label>
            ))}
          </div>
          <div className="flex items-center gap-1.5 p-2 border-t">
            <input value={custom} onChange={e => setCustom(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
              placeholder="Type your own…"
              className="flex-1 h-8 px-2 text-xs rounded-md border bg-background outline-none focus:ring-1 focus:ring-teal-500"
              data-testid={`${testId}-custom-input`} />
            <button type="button" onClick={addCustom} disabled={!custom.trim()}
              className="h-8 px-2.5 rounded-md text-xs font-bold bg-teal-600 text-white disabled:opacity-40"
              data-testid={`${testId}-custom-add`}>Add</button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function GeoCascade({ country, state, district, onChange, className = '', multi = false }) {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  useEffect(() => { getCountries().then(({ data }) => setCountries(data || [])).catch(() => {}); }, []);
  useEffect(() => {
    getStates(country || undefined).then(({ data }) => setStates(data || [])).catch(() => setStates([]));
  }, [country]);
  useEffect(() => {
    if (!state) { setDistricts([]); return; }
    const stateNames = multi ? state.split(',').map(s => s.trim()).filter(Boolean) : [state];
    Promise.all(stateNames.map(s => getDistricts(s).then(({ data }) => (data || []).map(d => d.name || d)).catch(() => [])))
      .then(lists => setDistricts([...new Set(lists.flat())]));
  }, [state, multi]);
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-3 gap-2 ${className}`} data-testid="geo-cascade">
      <div>
        <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Country</label>
        <Select value={country || undefined} onValueChange={v => onChange({ country: v, state: '', district: '' })}>
          <SelectTrigger data-testid="geo-country"><SelectValue placeholder="Country" /></SelectTrigger>
          <SelectContent>
            {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {multi ? (
        <>
          <GeoMultiPick label="State" options={states} value={state} disabled={!country}
            placeholder={country ? 'Select states' : 'Select country first'} testId="geo-state"
            onChange={v => onChange({ country, state: v, district: '' })} />
          <GeoMultiPick label="District" options={districts} value={district} disabled={!state}
            placeholder={state ? 'All districts' : 'Select state first'} testId="geo-district"
            onChange={v => onChange({ country, state, district: v })} />
        </>
      ) : (
        <>
      <div>
        <label className="text-[11px] font-medium text-muted-foreground mb-1 block">State</label>
        <Select value={state || undefined} onValueChange={v => onChange({ country, state: v, district: '' })} disabled={!country}>
          <SelectTrigger data-testid="geo-state"><SelectValue placeholder={country ? 'State' : 'Select country first'} /></SelectTrigger>
          <SelectContent>
            {states.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-[11px] font-medium text-muted-foreground mb-1 block">District</label>
        <Select value={district || 'none'} onValueChange={v => onChange({ country, state, district: v === 'none' ? '' : v })} disabled={!state}>
          <SelectTrigger data-testid="geo-district"><SelectValue placeholder={state ? 'District' : 'Select state first'} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— all districts —</SelectItem>
            {districts.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
        </>
      )}
    </div>
  );
}

export function TerritoryCascade({ value, onChange, showCountry = true, className = '' }) {
  const { country = '', state = '', district = '', jamoat = '', village = '', mahalla = '' } = value || {};
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [districtOpts, setDistrictOpts] = useState([]);
  const [jamoatOpts, setJamoatOpts] = useState([]);
  const [villageOpts, setVillageOpts] = useState([]);
  const [mahallaOpts, setMahallaOpts] = useState([]);
  const split = (v) => (v || '').split(',').map(s => s.trim()).filter(Boolean);

  useEffect(() => { if (showCountry) getCountries().then(({ data }) => setCountries(data || [])).catch(() => {}); }, [showCountry]);
  useEffect(() => {
    getStates(country || undefined).then(({ data }) => setRegions(data || [])).catch(() => setRegions([]));
  }, [country]);
  useEffect(() => {
    const names = split(state);
    if (!names.length) { setDistrictOpts([]); return; }
    Promise.all(names.map(s => getDistricts(s).then(({ data }) => (data || []).map(d => d.name || d)).catch(() => [])))
      .then(lists => setDistrictOpts([...new Set(lists.flat())]));
  }, [state]);
  useEffect(() => {
    if (!split(district).length) { setJamoatOpts([]); return; }
    getGeoUnits('jamoat', district).then(({ data }) => setJamoatOpts((data || []).map(d => d.name))).catch(() => setJamoatOpts([]));
  }, [district]);
  useEffect(() => {
    if (!split(jamoat).length) { setVillageOpts([]); return; }
    getGeoUnits('village', jamoat).then(({ data }) => setVillageOpts((data || []).map(d => d.name))).catch(() => setVillageOpts([]));
  }, [jamoat]);
  useEffect(() => {
    if (!split(village).length) { setMahallaOpts([]); return; }
    getGeoUnits('mahalla', village).then(({ data }) => setMahallaOpts((data || []).map(d => d.name))).catch(() => setMahallaOpts([]));
  }, [village]);

  const upd = (patch) => onChange({ country, state, district, jamoat, village, mahalla, ...patch });
  const persistGeo = (type, parentVal) => async (name) => {
    const parent = split(parentVal)[0] || '';
    const { data } = await addGeoUnit({ type, name, parent });
    return data;
  };

  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 gap-2 ${className}`} data-testid="territory-cascade">
      {showCountry && (
        <div>
          <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Country</label>
          <Select value={country || undefined} onValueChange={v => upd({ country: v, state: '', district: '', jamoat: '', village: '', mahalla: '' })}>
            <SelectTrigger data-testid="geo-country"><SelectValue placeholder="Country" /></SelectTrigger>
            <SelectContent>
              {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <GeoMultiPick label="Region / Province" options={regions} value={state} disabled={showCountry && !country}
        placeholder={!showCountry || country ? 'Select regions' : 'Select country first'} testId="geo-state"
        onChange={v => upd({ state: v })} />
      <GeoMultiPick label="District / City" options={districtOpts} value={district} disabled={!state}
        placeholder={state ? 'Select districts / cities' : 'Select region first'} testId="geo-district"
        onChange={v => upd({ district: v })} />
      <GeoMultiPick label="Jamoat" options={jamoatOpts} value={jamoat} disabled={!district}
        placeholder={district ? 'Select jamoats' : 'Select district first'} testId="geo-jamoat"
        onChange={v => upd({ jamoat: v })} onAddCustom={persistGeo('jamoat', district)} />
      <GeoMultiPick label="Village / Settlement" options={villageOpts} value={village} disabled={!jamoat}
        placeholder={jamoat ? 'Select villages' : 'Select jamoat first'} testId="geo-village"
        onChange={v => upd({ village: v })} onAddCustom={persistGeo('village', jamoat)} />
      <GeoMultiPick label="Mahalla / Area (optional)" options={mahallaOpts} value={mahalla} disabled={!village}
        placeholder={village ? 'Select mahallas' : 'Select village first'} testId="geo-mahalla"
        onChange={v => upd({ mahalla: v })} onAddCustom={persistGeo('mahalla', village)} />
    </div>
  );
}

export function PartnerDot({ show, testId }) {
  if (!show) return null;
  return (
    <span
      className="inline-block w-2 h-2 rounded-full bg-amber-400 ring-1 ring-amber-500/50 shrink-0"
      data-testid={testId}
      title="Has partners"
    />
  );
}

function FilterChip({ icon: Icon, label, value, isActive, onClear, children, testId, locked }) {
  const [open, setOpen] = useState(false);

  if (locked) {
    return (
      <div
        className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border bg-primary/10 border-primary/30 text-primary text-xs font-medium cursor-default"
        data-testid={testId}
      >
        <Icon className="w-3 h-3" />
        <span className="max-w-[120px] truncate">{value}</span>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`inline-flex items-center gap-1.5 h-7 rounded-full border text-xs font-medium transition-all duration-150 hover:shadow-sm
            ${isActive
              ? 'bg-primary/10 border-primary/30 text-primary pl-2.5 pr-1.5'
              : 'bg-muted/50 border-border text-muted-foreground hover:bg-muted hover:text-foreground px-2.5'
            }`}
          data-testid={testId}
        >
          <Icon className="w-3 h-3 shrink-0" />
          <span className="max-w-[120px] truncate">{isActive ? value : label}</span>
          {isActive ? (
            <span
              role="button"
              className="ml-0.5 p-0.5 rounded-full hover:bg-primary/20 transition-colors"
              onClick={(e) => { e.stopPropagation(); onClear(); }}
              data-testid={`${testId}-clear`}
            >
              <X className="w-3 h-3" />
            </span>
          ) : (
            <ChevronDown className="w-3 h-3 shrink-0 opacity-50" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start" side="bottom" sideOffset={4}>
        <div className="max-h-[240px] overflow-y-auto py-1" onClick={() => setOpen(false)}>
          {children}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ChipOption({ selected, onClick, children, testId, hasPartner }) {
  return (
    <button
      className={`w-full text-left px-3 py-1.5 text-xs transition-colors hover:bg-accent flex items-center gap-1.5
        ${selected ? 'bg-primary/10 text-primary font-semibold' : 'text-foreground'}`}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      data-testid={testId}
    >
      <span className="flex-1 min-w-0 truncate">{children}</span>
      <PartnerDot show={hasPartner} testId={testId ? `${testId}-partner-dot` : undefined} />
    </button>
  );
}

export function StateDistrictFilter({
  countries = [], selectedCountry, setSelectedCountry,
  states = [], districts = [],
  selectedState, setSelectedState,
  selectedDistrict, setSelectedDistrict,
  scope, className = '', partnerGeo
}) {
  const { t } = useLanguage();
  const lockedState = scope && scope.state && !scope.isSuperAdmin;
  const lockedDistrict = scope && scope.district && !scope.isSuperAdmin;

  const countryActive = selectedCountry && selectedCountry !== 'all';
  const stateActive = selectedState && selectedState !== 'all';
  const districtActive = selectedDistrict && selectedDistrict !== 'all';

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`} data-testid="filter-chips">
      {/* Country chip */}
      <FilterChip
        icon={Building}
        label={t('allCountries')}
        value={countryActive ? selectedCountry : t('allCountries')}
        isActive={countryActive}
        onClear={() => setSelectedCountry('all')}
        testId="chip-country"
      >
        <ChipOption selected={!countryActive} onClick={() => setSelectedCountry('all')} testId="chip-country-all">
          {t('allCountries')}
        </ChipOption>
        {(countries || []).map(c => (
          <ChipOption key={c} selected={selectedCountry === c} onClick={() => setSelectedCountry(c)} testId={`chip-country-${c}`}>
            {c}
          </ChipOption>
        ))}
      </FilterChip>

      {/* State chip */}
      {lockedState ? (
        <FilterChip icon={Map} value={selectedState} isActive locked testId="chip-state" />
      ) : (
        <FilterChip
          icon={Map}
          label={t('allStates')}
          value={stateActive ? selectedState : t('allStates')}
          isActive={stateActive}
          onClear={() => { setSelectedState('all'); setSelectedDistrict('all'); }}
          testId="chip-state"
        >
          <ChipOption selected={!stateActive} onClick={() => { setSelectedState('all'); setSelectedDistrict('all'); }} testId="chip-state-all">
            {t('allStates')}
          </ChipOption>
          {(states || []).map(s => (
            <ChipOption key={s} selected={selectedState === s} onClick={() => { setSelectedState(s); setSelectedDistrict('all'); }} testId={`chip-state-${s}`} hasPartner={partnerGeo?.states?.has(s)}>
              {s}
            </ChipOption>
          ))}
        </FilterChip>
      )}

      {/* District chip */}
      {lockedDistrict ? (
        <FilterChip icon={MapPin} value={selectedDistrict} isActive locked testId="chip-district" />
      ) : (
        <FilterChip
          icon={MapPin}
          label={t('allDistricts')}
          value={districtActive ? selectedDistrict : t('allDistricts')}
          isActive={districtActive}
          onClear={() => setSelectedDistrict('all')}
          testId="chip-district"
        >
          <ChipOption selected={!districtActive} onClick={() => setSelectedDistrict('all')} testId="chip-district-all">
            {t('allDistricts')}
          </ChipOption>
          {(districts || []).map(d => (
            <ChipOption key={d.name} selected={selectedDistrict === d.name} onClick={() => setSelectedDistrict(d.name)} testId={`chip-district-${d.name}`} hasPartner={partnerGeo?.districts?.has(d.name)}>
              {d.name}{selectedState === 'all' ? ` (${d.state})` : ''}
            </ChipOption>
          ))}
        </FilterChip>
      )}
    </div>
  );
}
